import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../context/PortalAuthContext';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  CheckCircleOutline as ApproveIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { SignaturePad } from '../components/SignaturePad';
import { useTranslation } from 'react-i18next';
import { applyLanguage } from '../i18n';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

interface AgreementData {
  customerName: string;
  businessName: string;
  businessPhone?: string;
  businessEmail?: string;
  logoUrl?: string;
  locale?: string;
  customerLanguage?: string;
  agreementBody: string;
  status: string;
  signedAt?: string;
  signatureUrl?: string | null;
  requireSignature?: boolean;
  portalToken?: string;
}

type PageState = 'loading' | 'ready' | 'approved' | 'error' | 'already_signed';

export const AgreementApproval: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { authenticate } = usePortalAuth();
  const [agreement, setAgreement] = useState<AgreementData | null>(null);
  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setPageState('error');
      setErrorMessage(t('No agreement token provided.'));
      return;
    }

    fetch(`${API_BASE_URL}/agreement-approval/${token}`)
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({ message: t('Failed to load agreement') }));
          throw new Error(err.message);
        }
        return res.json();
      })
      .then((data) => {
        applyLanguage(data.customerLanguage || data.locale);
        setAgreement(data);
        // Sign them into the portal
        if (data.portalToken) {
          authenticate(data.portalToken);
        }
        if (data.status === 'signed') {
          setPageState('already_signed');
        } else {
          setPageState('ready');
        }
      })
      .catch((err) => {
        setPageState('error');
        setErrorMessage(err.message || t('Unable to load agreement.'));
      });
  }, [authenticate, t, token]);

  const handleApprove = async () => {
    if (!token || !agreed) return;
    if (agreement?.requireSignature && !signatureData) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/agreement-approval/${token}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature: signatureData || undefined }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: t('Failed to approve agreement') }));
        throw new Error(err.message);
      }
      setPageState('approved');
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Loading
  if (pageState === 'loading') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f7fa', p: 3 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  // Error
  if (pageState === 'error') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f7fa', p: 3 }}>
        <Paper sx={{ maxWidth: 480, width: '100%', p: 4, textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>{t('Unable to Load Agreement')}</Typography>
          <Typography color="text.secondary">{errorMessage || t('This link may be invalid.')}</Typography>
        </Paper>
      </Box>
    );
  }

  // Approved success
  if (pageState === 'approved') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f7fa', p: 3 }}>
        <Paper sx={{ maxWidth: 480, width: '100%', p: 4, textAlign: 'center' }}>
          <ApproveIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>{t('Agreement Approved!')}</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {t('Thank you for approving the service agreement. {{businessName}} has been notified and will be in touch.', { businessName: agreement?.businessName })}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            sx={{
              mt: 2,
              bgcolor: '#016ec3',
              textTransform: 'none',
              '&:hover': { bgcolor: '#016ec3', opacity: 0.9 },
            }}
          >
            {t('Go to Your Portal')}
          </Button>
          {agreement?.businessPhone && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t('Questions? Call {{phone}}', { phone: agreement.businessPhone })}
            </Typography>
          )}
        </Paper>
      </Box>
    );
  }

  // Already signed
  if (pageState === 'already_signed') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f7fa', p: 3 }}>
        <Paper sx={{ maxWidth: 480, width: '100%', p: 4, textAlign: 'center' }}>
          <ApproveIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>{t('Already Approved')}</Typography>
          <Typography color="text.secondary">
            {t('This service agreement has already been approved. No further action is needed.')}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Ready - show agreement with approve button
  if (!agreement) return null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', py: 4, px: 2 }}>
      <Box sx={{ maxWidth: 680, mx: 'auto' }}>
        {/* Business Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          {agreement.logoUrl && (
            <Box
              component="img"
              src={agreement.logoUrl}
              alt={agreement.businessName}
              sx={{ height: 96, maxWidth: 320, objectFit: 'contain', mb: 1 }}
            />
          )}
          <Typography variant="h6" fontWeight="bold">
            {agreement.businessName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('Service Agreement')}
          </Typography>
        </Box>

        <Paper sx={{ p: { xs: 3, sm: 4 } }}>
          {/* Customer greeting */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('Prepared for')}
          </Typography>
          <Typography fontWeight="medium" sx={{ mb: 3 }}>
            {agreement.customerName}
          </Typography>

          {/* Agreement body */}
          <Box
            sx={{
              p: 3,
              bgcolor: '#fafafa',
              borderRadius: 1,
              border: '1px solid #e0e0e0',
              mb: 3,
              maxHeight: 400,
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.7,
              fontSize: '0.95rem',
            }}
          >
            {agreement.agreementBody}
          </Box>

          {/* Error message */}
          {errorMessage && (
            <Typography color="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Typography>
          )}

          {/* Signature pad (only if required) */}
          {agreement.requireSignature && (
            <SignaturePad onSignatureChange={setSignatureData} />
          )}

          {/* Approval checkbox and button */}
          <FormControlLabel
            control={
              <Checkbox
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                sx={{ '&.Mui-checked': { color: '#016ec3' } }}
              />
            }
            label={
              <Typography variant="body2">
                {t('I have read and agree to the terms of this service agreement.')}
              </Typography>
            }
            sx={{ mt: 2, mb: 3 }}
          />

          <Button
            variant="contained"
            fullWidth
            onClick={handleApprove}
            disabled={!agreed || (agreement.requireSignature && !signatureData) || actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <ApproveIcon />}
            sx={{
              bgcolor: '#016ec3',
              py: 1.5,
              fontSize: '1rem',
              textTransform: 'none',
              '&:hover': { bgcolor: '#016ec3', opacity: 0.9 },
            }}
          >
            {t('Approve Agreement')}
          </Button>

          {/* Contact info */}
          {(agreement.businessPhone || agreement.businessEmail) && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 3, textAlign: 'center' }}
            >
              {t('Questions? Contact {{businessName}}', { businessName: agreement.businessName })}
              {agreement.businessPhone && ` ${t('at')} ${agreement.businessPhone}`}
              {agreement.businessEmail && ` ${t('or')} ${agreement.businessEmail}`}
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
};
